import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight,
  LayoutDashboard,
  Menu,
  Moon,
  Percent,
  Receipt,
  Settings,
  Sun,
  User,
  Users,
  CreditCard,
  Database,
  ShieldCheck,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/Modal';
import { ROUTES } from '@/constants/routes';
import { AdminThemeProvider, useAdminTheme } from '@/context/AdminThemeContext';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import { BrandLogo } from './LandingNavbar';

const navItems = [
  { to: ROUTES.adminRoot, label: 'Dashboard', end: true, icon: LayoutDashboard },
  { to: ROUTES.adminUsers, label: 'Users', icon: Users },
  { to: ROUTES.adminAirtimePricing, label: 'Airtime Pricing', icon: Percent },
  { to: ROUTES.adminDataPlans, label: 'Data Plans', icon: Database },
  { to: ROUTES.adminBillPricing, label: 'Bill Pricing', icon: Receipt },
  { to: ROUTES.adminVerificationPricing, label: 'Verification Pricing', icon: ShieldCheck },
  { to: ROUTES.adminVirtualAccounts, label: 'Virtual Accounts', icon: CreditCard },
  { to: ROUTES.adminTransactions, label: 'Transactions', icon: ArrowLeftRight },
  { to: ROUTES.adminSettings, label: 'Settings', icon: Settings },
  { to: ROUTES.adminProfile, label: 'Profile', icon: User },
];

function AdminLayoutInner() {
  const navigate = useNavigate();
  const { admin, logout } = useAdminAuth();
  const { theme, toggleTheme } = useAdminTheme();
  const shell = adminShell(theme);
  const { loading: loggingOut, run } = useAsyncAction();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  async function confirmLogout() {
    await run(async () => {
      await logout();
      navigate(ROUTES.adminLogin);
    });
  }

  function closeMobileNav() {
    setMobileNavOpen(false);
  }

  function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
    return (
      <nav className="space-y-1 px-3">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive ? shell.navActive : shell.navIdle,
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
    );
  }

  return (
    <div className={shell.page}>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 hidden w-64 border-r lg:block',
          shell.sidebar,
          shell.sidebarBorder,
        )}
      >
        <div className={cn('flex h-16 items-center border-b px-5', shell.sidebarBorder)}>
          <BrandLogo />
        </div>
        <div className={cn('px-5 py-3 text-xs font-semibold uppercase tracking-wide', shell.sidebarLabel)}>
          Admin Panel
        </div>
        <NavLinks />
      </aside>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className={cn('absolute inset-0', shell.overlay)}
            aria-label="Close menu"
            onClick={closeMobileNav}
          />
          <aside
            className={cn(
              'absolute inset-y-0 left-0 flex w-[min(18rem,85vw)] flex-col border-r',
              shell.sidebar,
              shell.sidebarBorder,
            )}
          >
            <div className={cn('flex h-16 items-center justify-between border-b px-4', shell.sidebarBorder)}>
              <BrandLogo />
              <button
                type="button"
                onClick={closeMobileNav}
                className={cn('rounded-lg p-2', shell.ghostBtn)}
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className={cn('px-4 py-3 text-xs font-semibold uppercase tracking-wide', shell.sidebarLabel)}>
              Admin Panel
            </div>
            <NavLinks onNavigate={closeMobileNav} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header
          className={cn(
            'sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b px-4 backdrop-blur sm:px-6',
            shell.header,
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className={cn('rounded-lg p-2 lg:hidden', shell.ghostBtn)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0 lg:hidden">
              <BrandLogo />
            </div>
            <p className="hidden text-sm font-semibold lg:block">Admin Panel</p>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={toggleTheme}
              className={cn('rounded-lg p-2', shell.ghostBtn)}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <span className={cn('hidden rounded-full px-3 py-1 text-xs font-semibold capitalize sm:inline', shell.roleBadge)}>
              {admin?.role.replace('_', ' ')}
            </span>
            <Button
              variant="outline"
              size="sm"
              className={shell.outlineBtn}
              onClick={() => setShowLogoutConfirm(true)}
            >
              Logout
            </Button>
          </div>
        </header>

        <main className="p-4 pb-8 sm:p-6">
          <Outlet />
        </main>
      </div>

      <ConfirmDialog
        open={showLogoutConfirm}
        title="Logout?"
        message="Are you sure you want to sign out of the admin panel?"
        confirmLabel="Logout"
        variant="danger"
        loading={loggingOut}
        onConfirm={() => void confirmLogout()}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </div>
  );
}

export function AdminLayout() {
  return (
    <AdminThemeProvider>
      <AdminLayoutInner />
    </AdminThemeProvider>
  );
}
